// src/pages/tender/VendorDashboard.js

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import toast, { Toaster } from "react-hot-toast";
import { db } from "../../firebaseConfig";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const formatINR = (value) =>
  `₹ ${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

function PortalIcon({ name, className = "h-6 w-6" }) {
  const props = {
    className,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  const paths = {
    applications: (
      <>
        <path d="M6 3h9l3 3v15H6zM14 3v4h4" />
        <path d="M9 12h6M9 16h6" />
      </>
    ),
    submitted: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    awarded: (
      <>
        <path d="M8 4h8v5a4 4 0 0 1-8 0zM10 13v4M14 13v4M8 21h8M10 17h4" />
        <path d="M8 6H5v2a3 3 0 0 0 3 3M16 6h3v2a3 3 0 0 1-3 3" />
      </>
    ),
    rejected: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m9 9 6 6M15 9l-6 6" />
      </>
    ),
    ratio: <path d="M4 19V9M10 19V5M16 19v-7M22 19H2" />,
    tenders: (
      <>
        <path d="M4 20V8l8-4 8 4v12M3 20h18" />
        <path d="M8 12h8M8 16h8" />
      </>
    ),
    contractor: (
      <>
        <path d="m4 19 8-14 8 14M7 14h10M9 10h6M3 19h18" />
      </>
    ),
    profile: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
      </>
    ),
    refresh: (
      <>
        <path d="M20 6v5h-5M4 18v-5h5" />
        <path d="M18.5 9A7 7 0 0 0 6 6.5L4 9M5.5 15A7 7 0 0 0 18 17.5l2-2.5" />
      </>
    ),
    lock: (
      <>
        <rect x="5" y="10" width="14" height="11" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </>
    ),
  };

  return <svg {...props}>{paths[name] || paths.applications}</svg>;
}

function Badge({ color = "gray", children }) {
  const styles = {
    green: "border-emerald-300 bg-emerald-50 text-emerald-800",
    yellow: "border-amber-300 bg-amber-50 text-amber-800",
    red: "border-red-300 bg-red-50 text-red-700",
    gray: "border-slate-300 bg-slate-50 text-slate-700",
    blue: "border-blue-300 bg-blue-50 text-blue-800",
    dark: "border-slate-900 bg-slate-900 text-white",
  };

  return (
    <span
      className={cx(
        "inline-flex items-center justify-center rounded-md border px-2 py-1 text-[9px] font-extrabold leading-none",
        styles[color] || styles.gray
      )}
    >
      {children}
    </span>
  );
}

function StatCard({ label, value, note, icon }) {
  return (
    <div className="relative min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-[0_2px_12px_rgba(15,23,42,0.05)]">
      <div className="absolute inset-y-0 left-0 w-1 bg-emerald-600" />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-extrabold uppercase tracking-[0.08em] text-slate-500">
            {label}
          </div>
          <div className="mt-1 break-words text-xl font-extrabold leading-tight text-slate-950 2xl:text-2xl">
            {value}
          </div>
          <div className="mt-1 text-[10px] font-medium text-slate-400">{note}</div>
        </div>
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-white">
          <PortalIcon name={icon} className="h-5 w-5" />
        </span>
      </div>
    </div>
  );
}

function ActionCard({ icon, title, description, note, onClick, disabled = false }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cx(
        "group min-w-0 rounded-xl border bg-white p-4 text-left shadow-sm transition focus:outline-none focus:ring-2 focus:ring-emerald-500",
        disabled
          ? "cursor-not-allowed border-slate-200 opacity-65"
          : "border-slate-200 hover:-translate-y-0.5 hover:border-emerald-400 hover:shadow-md"
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cx(
            "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white",
            disabled ? "bg-slate-400" : "bg-slate-900 group-hover:bg-emerald-700"
          )}
        >
          <PortalIcon name={disabled ? "lock" : icon} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-extrabold text-slate-950">{title}</span>
          <span className="mt-1 block text-[10px] font-medium leading-4 text-slate-500">
            {description}
          </span>
        </span>
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-[9px] font-extrabold uppercase tracking-[0.08em] text-emerald-700">
        <span className={disabled ? "text-slate-400" : ""}>{note}</span>
        {!disabled && <span aria-hidden="true">→</span>}
      </div>
    </button>
  );
}

function computeStatus(startISO, endISO) {
  if (!startISO || !endISO) return "draft";

  const now = Date.now();
  const start = new Date(startISO).getTime();
  const end = new Date(endISO).getTime();

  if (now < start) return "upcoming";
  if (now <= end) return "running";
  return "expired";
}

function statusColor(status) {
  const value = String(status || "").toLowerCase();
  if (value === "approved" || value === "awarded") return "green";
  if (value === "rejected") return "red";
  if (value === "submitted") return "blue";
  if (value === "pending") return "yellow";
  return "gray";
}

function ApplicationCard({ application, navigate }) {
  const applicationStatus = String(application.status || "submitted").toLowerCase();
  const feeStatus = String(application.tenderFeeStatus || "pending").toLowerCase();
  const emdStatus = String(application.emdStatus || "pending").toLowerCase();

  return (
    <article className="min-w-0 max-w-full overflow-hidden rounded-xl border border-slate-300 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-3 border-b border-slate-200 bg-slate-50 p-4">
        <div className="min-w-0">
          <h3 className="break-words text-sm font-extrabold text-slate-950">
            {application.tenderTitle || "Tender"}
          </h3>
          <p className="mt-1 text-[10px] font-semibold text-slate-500">
            {application.tenderOrg || "—"} · {application.tenderCity || "—"}
          </p>
          <p className="mt-1 break-all font-mono text-[9px] text-slate-400">
            {application.tenderId || "—"}
          </p>
        </div>
        <Badge color={application.isAwarded ? "green" : statusColor(applicationStatus)}>
          {application.isAwarded ? "L1 Awarded" : application.status || "Submitted"}
        </Badge>
      </div>

      <div className="grid grid-cols-2 text-[10px]">
        <div className="border-b border-r border-slate-200 p-3">
          <div className="font-bold uppercase tracking-wide text-slate-400">Effective Amount</div>
          <div className="mt-1 break-words font-extrabold text-slate-900">
            {formatINR(application.effectiveAmount || 0)}
          </div>
        </div>
        <div className="border-b border-slate-200 p-3">
          <div className="font-bold uppercase tracking-wide text-slate-400">Rank</div>
          <div className="mt-1"><Badge color={application.isAwarded ? "green" : "gray"}>{application.rankLabel || "—"}</Badge></div>
        </div>
        <div className="border-r border-slate-200 p-3">
          <div className="font-bold uppercase tracking-wide text-slate-400">Tender Fee</div>
          <div className="mt-1 font-extrabold capitalize text-slate-800">{feeStatus}</div>
        </div>
        <div className="p-3">
          <div className="font-bold uppercase tracking-wide text-slate-400">EMD</div>
          <div className="mt-1 font-extrabold capitalize text-slate-800">{emdStatus}</div>
        </div>
      </div>

      <div className={cx("grid gap-3 p-4", application.isAwarded ? "grid-cols-2" : "grid-cols-1")}>
        <button
          type="button"
          onClick={() => navigate(`/tender/apply?tid=${application.tenderId}`)}
          className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-[10px] font-extrabold text-emerald-800 hover:bg-emerald-100"
        >
          View / Reprint
        </button>
        {application.isAwarded && (
          <button
            type="button"
            onClick={() => navigate("/tender/contractor")}
            className="rounded-lg bg-slate-900 px-3 py-2.5 text-[10px] font-extrabold text-white hover:bg-emerald-800"
          >
            Contractor Workspace
          </button>
        )}
      </div>
    </article>
  );
}

function StateScreen({ type, title, description, onLogout }) {
  const isLoading = type === "loading";
  const accent = type === "error" ? "text-red-700" : type === "pending" ? "text-amber-700" : "text-emerald-700";

  return (
    <div
      className="flex min-h-screen w-full max-w-full items-center justify-center overflow-x-hidden bg-slate-100 px-4"
      style={{ fontFamily: 'Inter, "Noto Sans", "Segoe UI", Roboto, Arial, sans-serif' }}
    >
      <Toaster position="top-center" />
      <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
        <div className="h-1 bg-gradient-to-r from-orange-500 via-white to-emerald-600" />
        <div className="p-7 text-center">
          <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full border-2 border-emerald-700 text-[11px] font-black text-emerald-800">
            SAAM
          </span>
          <div className="mt-5 text-[9px] font-extrabold uppercase tracking-[0.18em] text-slate-400">
            Tender Vendor Portal
          </div>
          <h1 className={cx("mt-2 text-lg font-extrabold", accent)}>{title}</h1>
          <p className="mx-auto mt-3 max-w-md text-xs font-medium leading-5 text-slate-500">
            {description}
          </p>
          {isLoading && (
            <div className="mx-auto mt-5 h-1.5 w-40 overflow-hidden rounded-full bg-slate-200">
              <div className="h-full w-2/3 animate-pulse rounded-full bg-emerald-600" />
            </div>
          )}
          {!isLoading && onLogout && (
            <button
              type="button"
              onClick={onLogout}
              className="mt-6 rounded-lg bg-slate-900 px-5 py-2.5 text-[10px] font-extrabold text-white hover:bg-slate-800"
            >
              Logout / Change Account
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function VendorDashboard() {
  const navigate = useNavigate();
  const auth = useMemo(() => getAuth(), []);

  const [user, setUser] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [loadingVendor, setLoadingVendor] = useState(true);
  const [apps, setApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(true);
  const [activeTab, setActiveTab] = useState("applications");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        toast.error("Please login as Tender Vendor first.");
        navigate("/tender?tab=login", { replace: true });
        return;
      }
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  useEffect(() => {
    const loadVendor = async () => {
      if (!user) {
        setVendor(null);
        setLoadingVendor(false);
        return;
      }

      try {
        setLoadingVendor(true);
        const snapshot = await getDoc(doc(db, "vendors", user.uid));

        if (!snapshot.exists()) {
          toast.error("Vendor profile not found.");
          setVendor(null);
        } else {
          setVendor({ id: snapshot.id, ...snapshot.data() });
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to load vendor profile.");
        setVendor(null);
      } finally {
        setLoadingVendor(false);
      }
    };

    loadVendor();
  }, [user]);

  const loadApps = async () => {
    if (!user) {
      setApps([]);
      setLoadingApps(false);
      return;
    }

    try {
      setLoadingApps(true);
      const applicationQuery = query(
        collection(db, "tender_applications"),
        where("vendorUid", "==", user.uid)
      );
      const applicationSnapshot = await getDocs(applicationQuery);
      const rows = [];

      for (const applicationDocument of applicationSnapshot.docs) {
        const application = applicationDocument.data();
        let tender = {};

        try {
          const tenderSnapshot = await getDoc(doc(db, "tenders", application.tenderId));
          tender = tenderSnapshot.exists()
            ? { id: tenderSnapshot.id, ...tenderSnapshot.data() }
            : {};
        } catch (error) {
          console.warn("Tender read failed:", application.tenderId, error);
        }

        const tenderStatus = computeStatus(tender.startAt, tender.endAt);
        const isAwarded =
          tender.awardedVendorUid &&
          String(tender.awardedVendorUid) === String(user.uid);

        rows.push({
          id: applicationDocument.id,
          ...application,
          tenderId: application.tenderId,
          tenderTitle:
            tender.title || tender.projectName || application.tenderTitle || "",
          tenderCity: tender.city || tender.district || "",
          tenderOrg: tender.ownerCompany || tender.pacsName || "",
          tenderStatus,
          isAwarded,
          awardedAmount:
            tender.awardedAmount || tender.awardedValue || null,
          baseAmount: tender.baseAmount || tender.amount || 0,
          startAt: tender.startAt || "",
          endAt: tender.endAt || "",
        });
      }

      rows.sort(
        (first, second) =>
          (second.submittedAt?.seconds || second.createdAt?.seconds || 0) -
          (first.submittedAt?.seconds || first.createdAt?.seconds || 0)
      );

      setApps(rows);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load applications.");
      setApps([]);
    } finally {
      setLoadingApps(false);
    }
  };

  useEffect(() => {
    loadApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const totals = useMemo(() => {
    const total = apps.length;
    const awarded = apps.filter((application) => application.isAwarded).length;
    const rejected = apps.filter(
      (application) => String(application.status || "").toLowerCase() === "rejected"
    ).length;
    const submitted = apps.filter(
      (application) =>
        String(application.status || "submitted").toLowerCase() === "submitted"
    ).length;
    const running = apps.filter(
      (application) => application.tenderStatus === "running"
    ).length;
    const winRatio = total > 0 ? Math.round((awarded / total) * 100) : 0;

    return { total, awarded, rejected, submitted, running, winRatio };
  }, [apps]);

  const filteredApps = useMemo(() => {
    let list = [...apps];

    if (activeTab === "awarded") {
      list = list.filter((application) => application.isAwarded);
    }
    if (activeTab === "submitted") {
      list = list.filter(
        (application) =>
          String(application.status || "submitted").toLowerCase() === "submitted"
      );
    }
    if (activeTab === "rejected") {
      list = list.filter(
        (application) =>
          String(application.status || "").toLowerCase() === "rejected"
      );
    }

    if (statusFilter !== "all") {
      list = list.filter((application) => {
        const applicationStatus = String(
          application.status || "submitted"
        ).toLowerCase();
        if (statusFilter === "awarded") return application.isAwarded;
        return applicationStatus === statusFilter;
      });
    }

    const needle = searchText.trim().toLowerCase();
    if (needle) {
      list = list.filter((application) =>
        [
          application.tenderTitle,
          application.tenderCity,
          application.tenderOrg,
          application.tenderId,
          application.status,
          application.rankLabel,
        ]
          .map((value) => String(value || "").toLowerCase())
          .join("|")
          .includes(needle)
      );
    }

    return list;
  }, [apps, activeTab, statusFilter, searchText]);

  const doLogout = async () => {
    await signOut(auth);
    navigate("/tender?tab=login", { replace: true });
  };

  if (!user || loadingVendor) {
    return (
      <StateScreen
        type="loading"
        title="Loading Vendor Dashboard"
        description="Please wait while your vendor profile and tender access are being verified."
      />
    );
  }

  if (!vendor) {
    return (
      <StateScreen
        type="error"
        title="Vendor Access Not Found"
        description="Your login is not linked to an active Tender Vendor profile. Please contact the Shine Agri Tender Cell."
        onLogout={doLogout}
      />
    );
  }

  const vendorStatus = String(vendor.status || "pending").toLowerCase();
  if (vendorStatus !== "approved" && vendorStatus !== "active") {
    return (
      <StateScreen
        type="pending"
        title="Vendor Registration Pending"
        description="Your tender vendor registration is not yet fully approved. Tenders and awarded project access will be enabled after approval by the Shine Agri Tender Cell."
        onLogout={doLogout}
      />
    );
  }

  const profile = vendor.profile || {};
  const address = profile.address || {};
  const companyName = profile.companyName || "Vendor Company";
  const vendorEmail = vendor.auth?.email || user.email || "—";
  const vendorId = vendor.vendorId || vendor.id || "—";
  const primaryPhone =
    profile.contactPhone || profile.contact?.phone || vendor.mobile || "—";
  const hasContractorAccess = totals.awarded > 0;

  const tabs = [
    { key: "applications", label: "My Applications", count: apps.length },
    { key: "awarded", label: "L1 Awarded", count: totals.awarded },
    { key: "submitted", label: "Submitted", count: totals.submitted },
    { key: "rejected", label: "Rejected", count: totals.rejected },
  ];

  return (
    <div
      className="min-h-screen w-full max-w-full overflow-x-hidden bg-slate-100 text-slate-900"
      style={{
        maxWidth: "100vw",
        fontFamily:
          'Inter, "Noto Sans", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      }}
    >
      <Toaster position="top-center" />
      <div className="h-1 bg-gradient-to-r from-orange-500 via-white to-emerald-600" />

      <div className="border-b border-white/10 bg-[#0b1f3a] px-4 py-1.5 text-white lg:px-6">
        <div className="flex items-center justify-between gap-4 text-[8px] font-bold uppercase tracking-[0.12em] sm:text-[9px]">
          <span className="truncate text-slate-200">
            Ministry Registered Multistate Cooperative Society
          </span>
          <span className="hidden shrink-0 text-emerald-300 sm:inline">
            Reg. No. MSCS/CR/1572/2024 · Secure Digital Portal
          </span>
        </div>
      </div>

      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur">
        <div className="flex min-h-[72px] w-full min-w-0 max-w-full items-center justify-between gap-4 px-4 py-3 lg:px-6">
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex min-w-0 items-center gap-3 text-left"
          >
            <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-[3px] border-double border-emerald-800 bg-white text-[10px] font-black tracking-tight text-emerald-900 shadow-sm">
              SAAM
            </span>
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-extrabold text-[#0b1f3a] sm:text-[15px]">
                Integrated Tender & Vendor Management Portal
              </span>
              <span className="mt-0.5 block truncate text-[9px] font-bold uppercase tracking-[0.12em] text-emerald-700 sm:text-[10px]">
                Shine Agri Allied & Marketing MSCS Ltd.
              </span>
              <span className="mt-0.5 hidden text-[8px] font-semibold text-slate-400 lg:block">
                Tender Application · Evaluation · Award · Project Execution
              </span>
            </span>
          </button>

          <div className="flex shrink-0 items-center gap-2">
            {hasContractorAccess && (
              <button
                type="button"
                onClick={() => navigate("/tender/contractor")}
                className="hidden rounded-lg bg-[#0b1f3a] px-4 py-2.5 text-[10px] font-extrabold text-white hover:bg-emerald-800 lg:inline-flex"
              >
                Contractor Workspace
              </button>
            )}
            <button
              type="button"
              onClick={loadApps}
              className="hidden rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[10px] font-extrabold text-slate-700 hover:bg-slate-50 sm:inline-flex"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="hidden rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[10px] font-extrabold text-slate-700 hover:bg-slate-50 md:inline-flex"
            >
              Main Website
            </button>
            <button
              type="button"
              onClick={doLogout}
              className="rounded-lg bg-red-600 px-4 py-2.5 text-[10px] font-extrabold text-white hover:bg-red-700"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="w-full min-w-0 max-w-full overflow-x-hidden px-3 py-5 sm:px-4 lg:px-6 lg:py-6">
        <section className="overflow-hidden rounded-2xl bg-slate-900 text-white shadow-xl">
          <div className="grid gap-6 px-5 py-7 lg:grid-cols-[minmax(0,1fr)_420px] lg:items-center lg:px-8">
            <div className="min-w-0">
              <div className="text-[9px] font-extrabold uppercase tracking-[0.2em] text-emerald-300">
                Integrated Tender & Vendor Management System
              </div>
              <h1 className="mt-2 break-words text-2xl font-extrabold leading-tight lg:text-3xl">
                Vendor Service Dashboard
              </h1>
              <p className="mt-2 max-w-3xl text-xs font-medium leading-5 text-slate-300">
                Authorized portal for {companyName}. Manage tender applications, fee and EMD status, evaluation results and awarded project access through one controlled digital workflow.
              </p>
              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-[10px] font-semibold text-slate-300">
                <span>Vendor ID: <strong className="text-white">{vendorId}</strong></span>
                <span>Login: <strong className="text-white">{vendorEmail}</strong></span>
                <span className="text-emerald-300">● Approved Vendor Access</span>
              </div>
            </div>

            <div
              className={cx(
                "rounded-xl border p-4",
                hasContractorAccess
                  ? "border-emerald-400/40 bg-emerald-500/10"
                  : "border-white/15 bg-white/5"
              )}
            >
              <div className="flex items-start gap-3">
                <span
                  className={cx(
                    "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
                    hasContractorAccess
                      ? "bg-emerald-500 text-white"
                      : "bg-slate-700 text-slate-300"
                  )}
                >
                  <PortalIcon name={hasContractorAccess ? "contractor" : "lock"} />
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-extrabold">
                    {hasContractorAccess
                      ? "Contractor Workspace Active"
                      : "Contractor Workspace Locked"}
                  </div>
                  <div className="mt-1 text-[10px] font-medium leading-4 text-slate-300">
                    {hasContractorAccess
                      ? `${totals.awarded} awarded project(s). Measurement, RA bills, GST and payments are available.`
                      : "This workspace becomes available automatically after a tender is awarded to your vendor account."}
                  </div>
                </div>
              </div>
              <button
                type="button"
                disabled={!hasContractorAccess}
                onClick={() => navigate("/tender/contractor")}
                className={cx(
                  "mt-4 w-full rounded-lg px-4 py-2.5 text-[10px] font-extrabold",
                  hasContractorAccess
                    ? "bg-emerald-500 text-white hover:bg-emerald-400"
                    : "cursor-not-allowed bg-slate-700 text-slate-400"
                )}
              >
                {hasContractorAccess ? "Open Contractor Dashboard →" : "Available After Award"}
              </button>
            </div>
          </div>
        </section>

        <section className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          <StatCard label="Applications" value={totals.total} note="All time" icon="applications" />
          <StatCard label="Submitted" value={totals.submitted} note="In process" icon="submitted" />
          <StatCard label="L1 Awarded" value={totals.awarded} note="Contractor access" icon="awarded" />
          <StatCard label="Rejected" value={totals.rejected} note="Not selected" icon="rejected" />
          <StatCard label="Win Ratio" value={`${totals.winRatio}%`} note={`${totals.running} running tenders`} icon="ratio" />
        </section>

        <section className="mt-6 grid min-w-0 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <ActionCard
            icon="tenders"
            title="View Live Tenders"
            description="Browse currently available tenders and submit a new application."
            note="Open Tender List"
            onClick={() => navigate("/tender/list")}
          />
          <ActionCard
            icon="applications"
            title="My Applications"
            description="Review application status, ranking, fee and EMD information."
            note={`${totals.total} Applications`}
            onClick={() => {
              setActiveTab("applications");
              document.getElementById("vendor-applications")?.scrollIntoView({ behavior: "smooth" });
            }}
          />
          <ActionCard
            icon="contractor"
            title="Contractor Workspace"
            description="Measurement, construction progress, RA bills, GST and payment tracking."
            note={hasContractorAccess ? `${totals.awarded} Awarded Projects` : "Available After Award"}
            disabled={!hasContractorAccess}
            onClick={() => navigate("/tender/contractor")}
          />
          <ActionCard
            icon="profile"
            title="Vendor Profile"
            description="Review company identity, contact and registration details."
            note="Verified Vendor"
            onClick={() => navigate("/tender/profile")}
          />
        </section>

        <section id="vendor-applications" className="mt-6 scroll-mt-24">
          <div className="grid min-w-0 gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
            <aside className="min-w-0 space-y-4">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 bg-slate-900 px-4 py-3 text-white">
                  <div className="text-xs font-extrabold">Company Profile</div>
                  <div className="mt-1 text-[9px] font-medium text-slate-300">Approved tender vendor information</div>
                </div>
                <dl className="divide-y divide-slate-200 text-[10px]">
                  {[
                    ["Vendor ID", vendorId],
                    ["Company", companyName],
                    ["Company Type", profile.companyType || "—"],
                    ["Contact Person", profile.contactName || "—"],
                    ["Mobile", primaryPhone],
                    ["Email", vendorEmail],
                    ["District", profile.district || address.district || "—"],
                    ["State", profile.state || address.state || "—"],
                  ].map(([label, value]) => (
                    <div key={label} className="grid grid-cols-[105px_minmax(0,1fr)] gap-3 px-4 py-3">
                      <dt className="font-bold text-slate-400">{label}</dt>
                      <dd className="min-w-0 break-words text-right font-extrabold text-slate-800">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-xs font-extrabold text-slate-950">Tender Performance</div>
                <div className="mt-4 space-y-4">
                  <div>
                    <div className="flex items-center justify-between text-[10px] font-extrabold text-slate-600">
                      <span>Win Ratio</span><span>{totals.winRatio}%</span>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200">
                      <div className="h-full rounded-full bg-emerald-600" style={{ width: `${Math.max(0, Math.min(100, totals.winRatio))}%` }} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg bg-slate-50 p-3 text-center">
                      <div className="text-lg font-extrabold text-slate-950">{totals.running}</div>
                      <div className="text-[8px] font-bold uppercase text-slate-400">Running</div>
                    </div>
                    <div className="rounded-lg bg-emerald-50 p-3 text-center">
                      <div className="text-lg font-extrabold text-emerald-800">{totals.awarded}</div>
                      <div className="text-[8px] font-bold uppercase text-emerald-600">Awarded</div>
                    </div>
                  </div>
                </div>
              </div>
            </aside>

            <div className="min-w-0">
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div className="min-w-0">
                    <div className="text-[10px] font-extrabold uppercase tracking-[0.1em] text-slate-500">
                      Application Control Center
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {tabs.map((tab) => (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() => setActiveTab(tab.key)}
                          className={cx(
                            "rounded-lg border px-3 py-2 text-[10px] font-extrabold",
                            activeTab === tab.key
                              ? "border-emerald-700 bg-emerald-700 text-white"
                              : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                          )}
                        >
                          {tab.label} ({tab.count})
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-[180px_minmax(240px,1fr)_auto] lg:w-[620px]">
                    <select
                      value={statusFilter}
                      onChange={(event) => setStatusFilter(event.target.value)}
                      className="h-10 min-w-0 rounded-lg border border-slate-300 bg-white px-3 text-[10px] font-bold text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    >
                      <option value="all">All Status</option>
                      <option value="submitted">Submitted</option>
                      <option value="pending">Pending</option>
                      <option value="awarded">Awarded / L1</option>
                      <option value="rejected">Rejected</option>
                    </select>
                    <input
                      value={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                      placeholder="Search tender, PACS, district or ID..."
                      className="h-10 min-w-0 rounded-lg border border-slate-300 px-3 text-[10px] font-semibold text-slate-700 outline-none placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setSearchText("");
                        setStatusFilter("all");
                      }}
                      className="h-10 rounded-lg border border-slate-300 bg-slate-50 px-4 text-[9px] font-extrabold uppercase tracking-wider text-slate-700 hover:bg-slate-100"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-4 overflow-hidden rounded-xl border border-slate-300 bg-white shadow-sm">
                <div className="flex items-center justify-between gap-3 border-b border-slate-300 px-4 py-3">
                  <div>
                    <h2 className="text-sm font-extrabold text-slate-950">Tender Applications</h2>
                    <p className="mt-1 text-[9px] font-medium text-slate-500">
                      Ranking, fee, EMD and contractor workspace access.
                    </p>
                  </div>
                  <Badge color="gray">{filteredApps.length} Records</Badge>
                </div>

                {loadingApps ? (
                  <div className="py-16 text-center text-xs font-semibold text-slate-500">Loading applications...</div>
                ) : filteredApps.length === 0 ? (
                  <div className="py-16 text-center text-xs font-semibold text-slate-500">No tender applications found.</div>
                ) : (
                  <>
                    <div className="hidden lg:block">
                      <table className="w-full table-fixed border-collapse text-[9px] text-slate-700">
                        <colgroup>
                          <col style={{ width: "31%" }} />
                          <col style={{ width: "10%" }} />
                          <col style={{ width: "14%" }} />
                          <col style={{ width: "9%" }} />
                          <col style={{ width: "12%" }} />
                          <col style={{ width: "10%" }} />
                          <col style={{ width: "14%" }} />
                        </colgroup>
                        <thead>
                          <tr className="bg-slate-900 text-white">
                            {[
                              "Tender / Organization",
                              "District",
                              "Effective Amount",
                              "Rank",
                              "Status",
                              "Fee / EMD",
                              "Action",
                            ].map((heading) => (
                              <th key={heading} className="border border-slate-600 px-2 py-3 text-center font-extrabold uppercase leading-3 tracking-[0.03em]">
                                {heading}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredApps.map((application) => {
                            const applicationStatus = String(application.status || "submitted").toLowerCase();
                            const feeStatus = String(application.tenderFeeStatus || "pending").toLowerCase();
                            const emdStatus = String(application.emdStatus || "pending").toLowerCase();
                            const cell = "break-words border border-slate-300 px-2 py-3 align-top leading-4";

                            return (
                              <tr key={application.id} className="even:bg-slate-50 hover:bg-emerald-50">
                                <td className={cell}>
                                  <div className="font-extrabold text-slate-950">{application.tenderTitle || "—"}</div>
                                  <div className="mt-1 font-semibold text-slate-500">{application.tenderOrg || "—"}</div>
                                  <div className="mt-1 break-all font-mono text-[8px] text-slate-400">{application.tenderId || "—"}</div>
                                </td>
                                <td className={`${cell} text-center font-bold`}>{application.tenderCity || "—"}</td>
                                <td className={`${cell} text-right font-extrabold text-slate-950`}>{formatINR(application.effectiveAmount || 0)}</td>
                                <td className={`${cell} text-center`}><Badge color={application.isAwarded ? "green" : "gray"}>{application.rankLabel || "—"}</Badge></td>
                                <td className={`${cell} text-center`}><Badge color={application.isAwarded ? "green" : statusColor(applicationStatus)}>{application.isAwarded ? "L1 Awarded" : application.status || "Submitted"}</Badge></td>
                                <td className={`${cell} text-center font-bold capitalize`}>
                                  <div>Fee: {feeStatus}</div>
                                  <div>EMD: {emdStatus}</div>
                                </td>
                                <td className={`${cell} text-center`}>
                                  <div className="grid gap-1.5">
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/tender/apply?tid=${application.tenderId}`)}
                                      className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-2 text-[8px] font-extrabold text-emerald-800 hover:bg-emerald-100"
                                    >
                                      View / Reprint
                                    </button>
                                    {application.isAwarded && (
                                      <button
                                        type="button"
                                        onClick={() => navigate("/tender/contractor")}
                                        className="rounded-md bg-slate-900 px-2 py-2 text-[8px] font-extrabold text-white hover:bg-emerald-800"
                                      >
                                        Contractor Workspace
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="grid min-w-0 gap-4 p-4 md:grid-cols-2 lg:hidden">
                      {filteredApps.map((application) => (
                        <ApplicationCard
                          key={application.id}
                          application={application}
                          navigate={navigate}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="mt-8 border-t border-slate-200 bg-white px-4 py-4 text-center text-[9px] font-semibold text-slate-400">
        Shine Agri Allied & Marketing Multistate Cooperative Society Ltd. · Reg. No. MSCS/CR/1572/2024 · Integrated Tender & Vendor Management Portal
      </footer>
    </div>
  );
}
